import type { LayerKind, SubLayerType } from '@civilgrid/ui/components';
import type { MapboxSourceConfig, VectorLayer } from '~/store2/layers.data';

/**
 * Derive the Layer Library's presentation assets from a tile-server tiles-config
 * entry — the same session-cached response the Map renders from (`layerStore.tilesConfig`
 * / `layerStore.customLayersTilesConfig`, keyed by leaf layer id).
 *
 * Pure module: no React / no I/O — unit-testable like `helpers.ts`.
 */

/** Geometry the popover's live-map preview renders for a layer. */
export type PreviewGeometry = 'line' | 'point' | 'polygon' | 'circle';

/**
 * Style for the popover's live-map preview, derived from the first style layer of a
 * vector entry. `null` for raster entries (imaging placeholder instead)
 * and for layers with no config entry (default placeholder).
 */
export type LayerPreviewStyle = {
  geometry: PreviewGeometry;
  color: string;
  /** Literal fill paint (polygon/circle), verbatim — may be fully transparent. */
  fillColor?: string;
  /** Literal outline paint (polygon/circle), verbatim — may be fully transparent. */
  outlineColor?: string;
  /** Mapbox `line-dasharray`, present only for dashed lines. */
  dashArray?: number[];
};

/** Preview color when the entry's paint carries no literal color (e.g. an expression). */
export const PREVIEW_FALLBACK_COLOR = '#393b3d';

/** Paint keys that can carry a style layer's representative color, by layer type. */
const PAINT_COLOR_KEYS: Record<VectorLayer['type'], string[]> = {
  line: ['line-color'],
  fill: ['fill-color', 'fill-outline-color'],
  circle: ['circle-color', 'circle-stroke-color'],
  symbol: ['icon-color', 'text-color'],
};

function firstStyleLayer(entry: MapboxSourceConfig | undefined): VectorLayer | undefined {
  return entry?.type === 'vector' ? entry.layers[0] : undefined;
}

/** A line style layer is dashed when its dasharray has an actual pattern ([1] = solid). */
function lineDashArray(layer: VectorLayer | undefined): number[] | undefined {
  if (layer?.type !== 'line') return undefined;
  const dash = layer.paint?.['line-dasharray'];
  const isPattern = Array.isArray(dash) && dash.length >= 2 && dash.every((d) => typeof d === 'number');
  return isPattern ? dash : undefined;
}

/**
 * The `LayerKindIcon` kind for a layer (Name column):
 * custom dataset → `document` (custom layers only) ·
 * raster → `image` · line → `line`/`lineDashed` ·
 * fill → `areaFilled` when the paint carries a literal fill color, `area` when
 * outline-only · circle → `circle` · symbol → `stamp` ·
 * no entry / no style layers → `undefined` (default icon).
 */
export function deriveLayerKind(
  entry: MapboxSourceConfig | undefined,
  opts?: { customDataset?: boolean },
): LayerKind | undefined {
  if (opts?.customDataset) return 'document';
  if (!entry) return undefined;
  if (entry.type === 'raster') return 'image';
  const layer = entry.layers[0];
  switch (layer?.type) {
    case 'line':
      return lineDashArray(layer) ? 'lineDashed' : 'line';
    case 'fill':
      return typeof layer.paint?.['fill-color'] === 'string' ? 'areaFilled' : 'area';
    case 'circle':
      return 'circle';
    case 'symbol':
      return 'stamp';
    default:
      return undefined;
  }
}

/**
 * A paint color that renders nothing — alpha-0 `rgba()`/`hsla()` (comma or slash
 * syntax), `transparent`, or a 00-alpha hex. Skipped when picking a representative
 * color: e.g. digitized fills carry `fill-color: rgba(252, 199, 207, 0)` and draw via
 * their outline color instead.
 */
function isFullyTransparent(color: string): boolean {
  const normalized = color.trim().toLowerCase();
  if (normalized === 'transparent') return true;
  if (/^#[0-9a-f]{8}$/.test(normalized)) return normalized.slice(7) === '00';
  if (/^#[0-9a-f]{4}$/.test(normalized)) return normalized[4] === '0';
  const fn = normalized.match(/^(?:rgba?|hsla?)\((.*)\)$/);
  if (!fn) return false;
  const [, body] = fn;
  const slash = body.split('/');
  if (slash.length === 2) return Number.parseFloat(slash[1]) === 0;
  const parts = body.split(',');
  return parts.length === 4 && Number.parseFloat(parts[3]) === 0;
}

/**
 * The icon color for a layer, from its first style layer's paint — the first paint key
 * carrying a **visible** literal color (fully transparent values fall through to the
 * next key, e.g. an alpha-0 `fill-color` to `fill-outline-color`). `undefined` when no
 * key has one (data-driven expressions / all-transparent paint keep the glyph's default
 * color) and for raster entries (the `image` glyph keeps its fixed color).
 */
export function deriveLayerColor(entry: MapboxSourceConfig | undefined): string | undefined {
  const layer = firstStyleLayer(entry);
  if (!layer) return undefined;
  for (const key of PAINT_COLOR_KEYS[layer.type] ?? []) {
    const value = layer.paint?.[key];
    if (typeof value === 'string' && !isFullyTransparent(value)) return value;
  }
  return undefined;
}

/** A layer's fill + outline paint pair (fill/circle types carry both). */
export type LayerColorPair = {
  fill?: string;
  outline?: string;
};

/** The paint keys carrying a type's fill/outline pair (single-color types are not listed). */
const FILL_OUTLINE_KEYS: Partial<Record<VectorLayer['type'], { fill: string; outline: string }>> = {
  fill: { fill: 'fill-color', outline: 'fill-outline-color' },
  circle: { fill: 'circle-color', outline: 'circle-stroke-color' },
};

/**
 * Both paint colors of a layer's first style layer, **verbatim** — fully transparent
 * values pass through (the renderers draw them as-is; an alpha-0 fill draws nothing,
 * exactly like the map). Per slot, `undefined` means the paint key carries no literal
 * color (absent or a data-driven expression). Single-color types (line/symbol) populate
 * `fill` with their representative color.
 */
export function deriveLayerColors(entry: MapboxSourceConfig | undefined): LayerColorPair {
  const layer = firstStyleLayer(entry);
  if (!layer) return {};
  const keys = FILL_OUTLINE_KEYS[layer.type];
  if (!keys) return { fill: deriveLayerColor(entry) };
  const literal = (key: string) => {
    const value = layer.paint?.[key];
    return typeof value === 'string' ? value : undefined;
  };
  return { fill: literal(keys.fill), outline: literal(keys.outline) };
}

/**
 * The geometry/data type for the popover's "Included Sub-Layers" icons:
 * raster → `image` · line → `line` · fill → `polygon` · circle → `circle` ·
 * symbol → `point` · no entry → `undefined` (consumer picks the default).
 */
export function deriveSubLayerType(entry: MapboxSourceConfig | undefined): SubLayerType | undefined {
  if (!entry) return undefined;
  if (entry.type === 'raster') return 'image';
  switch (entry.layers[0]?.type) {
    case 'line':
      return 'line';
    case 'fill':
      return 'polygon';
    case 'circle':
      return 'circle';
    case 'symbol':
      return 'point';
    default:
      return undefined;
  }
}

/**
 * The popover preview's render style. `null` when there is nothing to draw — raster
 * entries (line color/style are null by decision; the popover shows the imaging
 * placeholder) and missing entries / entries without style layers (default placeholder).
 */
export function derivePreviewStyle(entry: MapboxSourceConfig | undefined): LayerPreviewStyle | null {
  const layer = firstStyleLayer(entry);
  if (!layer) return null;
  const geometry = ({ line: 'line', fill: 'polygon', circle: 'circle', symbol: 'point' } as const)[layer.type];
  if (!geometry) return null;
  // Polygon/circle carry the verbatim fill/outline pair; `color` stays the visible
  // single fallback (line/point, and either slot when its literal is absent).
  const pair = geometry === 'polygon' || geometry === 'circle' ? deriveLayerColors(entry) : {};
  return {
    geometry,
    color: deriveLayerColor(entry) ?? PREVIEW_FALLBACK_COLOR,
    fillColor: pair.fill,
    outlineColor: pair.outline,
    dashArray: lineDashArray(layer),
  };
}
